import type { Aircraft } from "../aircraft/Aircraft";
import type { Vec2 } from "../airport/AirportDefinition";
import type { ReleaseOutcome } from "../game/GameSession";

/**
 * Playing without a pointer.
 *
 * Tab picks an aircraft, the arrow keys steer a cursor that lays down the same
 * waypoints a drag would, Enter commits and Escape abandons. It drives the
 * session's existing `grab` / `drag` / `release` verbs, so a keyboard route is
 * indistinguishable from a drawn one once committed.
 */
export interface KeyboardTarget {
  readonly aircraft: readonly Aircraft[];
  grab(point: Vec2): boolean;
  drag(point: Vec2): boolean;
  release(): ReleaseOutcome;
  hoveredId: number | null;
}

export interface KeyboardControllerOptions {
  readonly target: EventTarget;
  readonly session: KeyboardTarget;
  /** World units travelled per arrow press. */
  readonly step?: number;
  readonly onSelect?: (aircraft: Aircraft | null) => void;
  readonly onRelease?: (outcome: ReleaseOutcome) => void;
  readonly onTogglePause?: () => void;
  readonly onRotate?: (direction: "left" | "right") => void;
  readonly onZoom?: (direction: "in" | "out") => void;
  readonly onToggleSound?: () => void;
}

const DEFAULT_STEP = 6;

/** Arrow and WASD both steer, so neither hand is required. */
const DIRECTIONS: Readonly<Record<string, Vec2>> = {
  ArrowUp: { x: 0, y: 1 },
  ArrowDown: { x: 0, y: -1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  w: { x: 0, y: 1 },
  s: { x: 0, y: -1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 },
};

export class KeyboardController {
  private selectedId: number | null = null;
  private cursor: Vec2 | null = null;
  private drawing = false;

  constructor(private readonly options: KeyboardControllerOptions) {
    options.target.addEventListener("keydown", this.handleKeyDown);
  }

  dispose(): void {
    this.options.target.removeEventListener("keydown", this.handleKeyDown);
  }

  /** The aircraft the keyboard is pointed at, if it is still flying. */
  get selected(): Aircraft | null {
    return (
      this.options.session.aircraft.find(
        (aircraft) =>
          aircraft.id === this.selectedId && aircraft.state === "flying",
      ) ?? null
    );
  }

  private selectable(): Aircraft[] {
    return this.options.session.aircraft.filter(
      (aircraft) => aircraft.state === "flying",
    );
  }

  /** Move the selection along the list, wrapping at both ends. */
  private cycle(offset: number): void {
    const candidates = this.selectable();
    if (candidates.length === 0) {
      this.clear();
      return;
    }

    const current = candidates.findIndex(
      (aircraft) => aircraft.id === this.selectedId,
    );
    const next =
      current === -1
        ? offset > 0
          ? 0
          : candidates.length - 1
        : (current + offset + candidates.length) % candidates.length;

    this.abandon();
    const aircraft = candidates[next]!;
    this.selectedId = aircraft.id;
    this.options.session.hoveredId = aircraft.id;
    this.options.onSelect?.(aircraft);
  }

  private clear(): void {
    this.abandon();
    this.selectedId = null;
    this.options.session.hoveredId = null;
    this.options.onSelect?.(null);
  }

  /** Drop a half-drawn route without committing it. */
  private abandon(): void {
    if (this.drawing) {
      this.options.session.release();
      this.drawing = false;
    }
    this.cursor = null;
  }

  private steer(direction: Vec2): void {
    const aircraft = this.selected;
    if (!aircraft) {
      return;
    }

    const step = this.options.step ?? DEFAULT_STEP;
    if (!this.drawing) {
      if (!this.options.session.grab({ ...aircraft.pos })) {
        return;
      }
      this.drawing = true;
      this.cursor = { ...aircraft.pos };
    }

    const cursor = this.cursor ?? { ...aircraft.pos };
    this.cursor = {
      x: cursor.x + direction.x * step,
      y: cursor.y + direction.y * step,
    };
    this.options.session.drag({ ...this.cursor });
  }

  private commit(): void {
    if (!this.drawing) {
      return;
    }
    this.drawing = false;
    this.cursor = null;
    this.options.onRelease?.(this.options.session.release());
  }

  private readonly handleKeyDown = (event: Event): void => {
    const key = event as KeyboardEvent;
    if (key.altKey || key.ctrlKey || key.metaKey) {
      return;
    }

    if (key.key === "Tab") {
      this.cycle(key.shiftKey ? -1 : 1);
      event.preventDefault();
      return;
    }

    const direction = DIRECTIONS[key.key];
    if (direction) {
      this.steer(direction);
      event.preventDefault();
      return;
    }

    switch (key.key) {
      case "Enter":
      case " ":
        this.commit();
        event.preventDefault();
        return;
      case "Escape":
        this.clear();
        event.preventDefault();
        return;
      case "p":
        this.options.onTogglePause?.();
        event.preventDefault();
        return;
      case "q":
        this.options.onRotate?.("left");
        event.preventDefault();
        return;
      case "e":
        this.options.onRotate?.("right");
        event.preventDefault();
        return;
      // Both faces of the key, so neither layout needs a shift: "+" and "="
      // sit together, as do "-" and "_".
      case "+":
      case "=":
        this.options.onZoom?.("in");
        event.preventDefault();
        return;
      case "-":
      case "_":
        this.options.onZoom?.("out");
        event.preventDefault();
        return;
      case "m":
        this.options.onToggleSound?.();
        event.preventDefault();
        return;
      default:
    }
  };
}
